import React, { useState, useEffect, useRef } from 'react';
import { Calendar, RefreshCw, BarChart3, Settings, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { syncVirtualContact } from '../../../api/contact';
import { fetchDashboardMetrics, fetchPropertyInteractions } from '../api/analyticsApi';
import KPISummaryCards from '../components/KPISummaryCards';
import FunnelChart from '../components/FunnelChart';
import TopEngagedBuyers from '../components/TopEngagedBuyers';
import SourceQualityChart from '../components/SourceQualityChart';
import PropertyInteractionStats from '../components/PropertyInteractionStats';
import EventDetailModal from '../components/EventDetailModal';
import BuyerActivityModal from '../components/BuyerActivityModal';
import EnquiryDetailModal from '../components/EnquiryDetailModal';
import BuyerEngagementModal, { ENGAGEMENT_VIEWS } from '../components/BuyerEngagementModal';
import PropertySavesModal from '../components/PropertySavesModal';
import SignupBreakdownModal from '../components/SignupBreakdownModal';
import BuyerAnalytics from '../components/BuyerAnalytics';
import RenterAnalytics from '../components/RenterAnalytics';
import LiveUsersBadge from '../components/LiveUsersBadge';
import LiveUsersModal from '../components/LiveUsersModal';
import OfferDetailModal from '../components/OfferDetailModal';
import SessionDetailModal from '../components/SessionDetailModal';
import InvestmentCalculatorAnalytics from '../components/InvestmentCalculatorAnalytics';
import MortgageCalculatorAnalytics from '../components/MortgageCalculatorAnalytics';
import SmsfAnalytics from '../components/SmsfAnalytics';
import ChatbotAnalyticsPanel from '../components/ChatbotAnalyticsPanel';
import AnalyticsExclusionsModal from '../components/AnalyticsExclusionsModal';
import AnalyticsExportModal from '../components/AnalyticsExportModal';
import { tabsFor, tabById, defaultTabFor, sectionForTab } from '../analyticsTabs';
import EngagementPanel from '../components/EngagementPanel';
import LocationAnalyticsPanel from '../components/LocationAnalyticsPanel';
import HeatmapPanel from '../components/HeatmapPanel';
import InvestorAnalyticsPanel from '../components/InvestorAnalyticsPanel';
import TargetingBriefPanel from '../components/TargetingBriefPanel';
import InvestorDemandIndexPanel from '../components/InvestorDemandIndexPanel';
import OffMarketDemandPanel from '../components/OffMarketDemandPanel';
import DailyReportPanel from '../components/DailyReportPanel';
import UserProfilesPanel from '../components/UserProfilesPanel';
import BuyerSearchStatusPanel from '../components/BuyerSearchStatusPanel';
import CostPerLeadPanel from '../components/CostPerLeadPanel';
import GeeVeeCategoriesPanel from '../components/GeeVeeCategoriesPanel';
import DomaInfluencedRevenue from '../components/DomaInfluencedRevenue';
import SuburbResearchPanel from '../components/SuburbResearchPanel';
import CommunicationsAnalyticsPanel from '../components/CommunicationsAnalyticsPanel';
import CampaignLeadsPanel from '../components/CampaignLeadsPanel';
import FacebookLeadsPanel from '../components/FacebookLeadsPanel';
import FindBuyersForProperty from '../components/FindBuyersForProperty';
import FindRentersForProperty from '../components/FindRentersForProperty';
import AgentAnalyticsPanel from '../components/AgentAnalyticsPanel';
import AgentUsagePanel from '../components/AgentUsagePanel';
import CrmOverviewPanel from '../components/CrmOverviewPanel';
import CrmSectionPanel from '../components/CrmSectionPanel';
import { hasAdminAccess, hasSuperAdminAccess } from "../../../utils/roleAccess";

const AnalyticsPage = ({ environment = 'portal', activeView: activeViewProp, onViewChange }) => {
    const { user } = useSelector((state) => state.auth);
    const isAdmin = hasAdminAccess(user);
    // The cut-down non-admin analytics view: sees only the Property / Buyer /
    // Renter tabs, with every admin-only tab and panel dropped. This is agents
    // AND PAs. A PA is not an 'agent' by role, so the old
    // `hasAgentAccess(user) && !hasAdminAccess(user)` test made them fall through
    // as a full admin and see all ten admin-only Portal Analytics tabs. CG,
    // 7 Sep 2026 (#12982366802): "Which analytics can agents see? All they need
    // is Property Analytics, Buyer Analytics & Renter analytics. The rest is just
    // for me the super admin." So gate on "not an admin" instead.
    const isAgent = !isAdmin;
    // False for a PLAIN ADMIN. Gates the CRM Analytics and Executive tabs, which
    // the backend also refuses them (core.permissions.IsSuperAdmin).
    const isSuperAdmin = hasSuperAdminAccess(user);
    const navigate = useNavigate();
    const location = useLocation();
    const [redirectingBuyer, setRedirectingBuyer] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [dashboardData, setDashboardData] = useState(null);
    // Bumped to force the Top Engaged Buyers panel (self-fetching) to reload on
    // a manual refresh; it also reloads automatically when the date range changes.
    const [buyerRefreshKey, setBuyerRefreshKey] = useState(0);
    const [propertyInteractions, setPropertyInteractions] = useState([]);
    // Dedicated loading flag for the Top Performing Properties panel so switching
    // the lifecycle tab (Current/Sold/Withdrawn) doesn't depend on the page-wide load.
    const [propertyLoading, setPropertyLoading] = useState(false);
    const [dateRange, setDateRange] = useState({
        start: '2020-01-01T00:00:00.000Z', // OVERALL_START — default to all-time
        end: new Date().toISOString()
    });
    const [selectedRange, setSelectedRange] = useState('overall');
    // The visible tab. The wrapper owns it (it also owns the Portal/CRM
    // environment, and switching environment has to reset the tab), so this
    // component is controlled — with a local fallback so AnalyticsPage still
    // works if it is ever rendered standalone.
    const initialView = new URLSearchParams(location.search).get('view');
    const [localView, setLocalView] = useState(
        initialView || defaultTabFor(environment, { isAgent, isSuperAdmin })
    );
    const activeView = activeViewProp || localView;
    const setActiveView = (id) => {
        setLocalView(id);
        if (onViewChange) onViewChange(id);
    };

    const visibleTabs = tabsFor(environment, { isAgent, isSuperAdmin });
    /**
     * Is this tab one the viewer is actually allowed to open?
     *
     * The render branches below used to test `!isAgent`, which was the same
     * question while "not an agent" meant "an admin, who sees everything". It
     * no longer does: a plain admin is not an agent AND cannot open a CRM or
     * Executive tab. Every CRM/Executive branch asks this instead, so a stale
     * `?view=` cannot mount a panel whose endpoint will 403.
     */
    const canSeeTab = (id) => visibleTabs.some((t) => t.id === id);
    const currentTab = tabById(activeView);
    const [propertySortBy, setPropertySortBy] = useState('demand_score');
    // Lifecycle filter for Top Performing Properties: current (live), sold, withdrawn
    const [propertyStatus, setPropertyStatus] = useState('current');
    const [propertySearch, setPropertySearch] = useState('');
    const [propertySearchDebounced, setPropertySearchDebounced] = useState('');
    
    // Modal states
    const [eventModalOpen, setEventModalOpen] = useState(false);
    const [eventModalConfig, setEventModalConfig] = useState({});
    const [buyerModalOpen, setBuyerModalOpen] = useState(false);
    const [buyerModalConfig, setBuyerModalConfig] = useState({});
    const [enquiryModalOpen, setEnquiryModalOpen] = useState(false);
    // Which Overview card's buyer list is open (a key of ENGAGEMENT_VIEWS), or null.
    const [engagementView, setEngagementView] = useState(null);
    const [savesOpen, setSavesOpen] = useState(false);
    const [signupBreakdownOpen, setSignupBreakdownOpen] = useState(false);
    const [liveUsersModalOpen, setLiveUsersModalOpen] = useState(false);
    const [offerModalOpen, setOfferModalOpen] = useState(false);
    const [sessionModalOpen, setSessionModalOpen] = useState(false);
    const [exclusionsModalOpen, setExclusionsModalOpen] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    // Bumped whenever the exclusion list changes, so the CRM panels refetch
    // instead of leaving a just-excluded account sitting in the table.
    const [exclusionsVersion, setExclusionsVersion] = useState(0);

    // Tracks the most-recent in-flight load so out-of-order responses are dropped
    // (e.g. user picks "Last 365 days" then quickly switches to "Last 1 day" —
    // the slower 365-day response must NOT overwrite the 1-day result).
    const loadTokenRef = useRef(0);
    // Tracks the most-recent property-interactions request (independent of the
    // page-wide token) and caches results per (range, sort, status) combination so
    // re-visiting a tab is instant instead of re-hitting the network each time.
    const propertyTokenRef = useRef(0);
    const propertyCacheRef = useRef({});
    const prefetchTimerRef = useRef(null);

    const rangeDaysFromSelection = () => {
        const rangeMs = new Date(dateRange.end) - new Date(dateRange.start);
        return Math.max(1, Math.ceil(rangeMs / (24 * 60 * 60 * 1000)));
    };

    // Report tab only — dashboard aggregation is heavy on "Overall" ranges.
    useEffect(() => {
        if (isAgent || activeView !== 'daily') return;
        loadDashboardMetrics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange, activeView, isAgent]);

    // Property interactions are only needed on the Property Analytics tab.
    useEffect(() => {
        if (activeView !== 'property') return;
        loadPropertyInteractions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeView, dateRange, propertySortBy, propertyStatus, propertySearchDebounced]);

    // Debounce property search so we don't hit the API on every keystroke.
    useEffect(() => {
        const timer = setTimeout(() => setPropertySearchDebounced(propertySearch.trim()), 300);
        return () => clearTimeout(timer);
    }, [propertySearch]);

    // Keep the selected tab valid for the current environment and role. Covers
    // an agent landing on an admin-only tab via ?view=, and the tab left over
    // after switching environment. Falls back to the first tab they can see.
    useEffect(() => {
        if (visibleTabs.some((t) => t.id === activeView)) return;
        const fallback = defaultTabFor(environment, { isAgent, isSuperAdmin });
        if (fallback && fallback !== activeView) setActiveView(fallback);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [environment, isAgent, isSuperAdmin, activeView]);

    const loadDashboardMetrics = async () => {
        const myToken = ++loadTokenRef.current;
        try {
            setRefreshing(true);
            const data = await fetchDashboardMetrics(dateRange.start, dateRange.end);
            if (myToken !== loadTokenRef.current) return;
            setDashboardData(data);
        } catch (error) {
            if (myToken !== loadTokenRef.current) return;
            console.error('fetchDashboardMetrics failed:', error);
            toast.error('Failed to load dashboard metrics');
        } finally {
            if (myToken === loadTokenRef.current) {
                setRefreshing(false);
            }
        }
    };

    const propertyCacheKey = (sort, statusVal, searchVal) =>
        `${dateRange.start}|${dateRange.end}|${sort}|${statusVal}|${searchVal || ''}`;

    // Low-level fetch that reads/writes the per-(range, sort, status, search) cache but does
    // NOT touch any display state. Used both for the visible tab and for warming the
    // cache of the other tabs in the background.
    const fetchAndCacheProperties = async (sort, statusVal, searchVal, { force = false } = {}) => {
        const cacheKey = propertyCacheKey(sort, statusVal, searchVal);
        if (!force && propertyCacheRef.current[cacheKey]) {
            return propertyCacheRef.current[cacheKey];
        }
        const data = await fetchPropertyInteractions(
            dateRange.start, dateRange.end, 50, sort, statusVal, searchVal
        );
        propertyCacheRef.current[cacheKey] = data;
        return data;
    };

    // After the visible tab is shown, quietly pre-load the other two lifecycle tabs
    // in the background (deferred so the first paint isn't competing with 2 extra
    // heavy aggregations on the same DB connection).
    const prefetchOtherStatuses = (currentStatus) => {
        if (prefetchTimerRef.current) clearTimeout(prefetchTimerRef.current);
        const others = ['current', 'sold', 'withdrawn'].filter(s => s !== currentStatus);
        prefetchTimerRef.current = setTimeout(async () => {
            for (const s of others) {
                try {
                    await fetchAndCacheProperties(propertySortBy, s, propertySearchDebounced);
                } catch (error) {
                    console.debug('Prefetch of property status failed:', s, error);
                }
            }
        }, 2000);
    };

    // Loads the visible Top Performing Properties list. Served instantly from cache
    // when available; otherwise fetches, then warms the other tabs in the background.
    const loadPropertyInteractions = async ({ force = false } = {}) => {
        const cacheKey = propertyCacheKey(propertySortBy, propertyStatus, propertySearchDebounced);

        if (!force && propertyCacheRef.current[cacheKey]) {
            setPropertyInteractions(propertyCacheRef.current[cacheKey]);
            setPropertyLoading(false);
            // Still ensure the other tabs are warm for instant switching.
            prefetchOtherStatuses(propertyStatus);
            return;
        }

        const myToken = ++propertyTokenRef.current;
        setPropertyLoading(true);
        try {
            const data = await fetchAndCacheProperties(
                propertySortBy, propertyStatus, propertySearchDebounced, { force }
            );
            // Drop the response if a newer property request has started since.
            if (myToken !== propertyTokenRef.current) return;
            setPropertyInteractions(data);
            prefetchOtherStatuses(propertyStatus);
        } catch (error) {
            if (myToken !== propertyTokenRef.current) return;
            console.error('fetchPropertyInteractions failed:', error);
            toast.error('Failed to load property interactions');
        } finally {
            if (myToken === propertyTokenRef.current) {
                setPropertyLoading(false);
            }
        }
    };

    const handleRefresh = () => {
        propertyCacheRef.current = {};
        if (!isAgent && activeView === 'daily') {
            loadDashboardMetrics();
        } else if (activeView === 'buyer') {
            setBuyerRefreshKey((k) => k + 1);
        } else if (activeView === 'property') {
            loadPropertyInteractions({ force: true });
        }
    };

    const RANGE_DAYS = {
        '1d': 1,
        '7d': 7,
        '30d': 30,
        '60d': 60,
        '90d': 90,
        '120d': 120,
        '180d': 180,
        '365d': 365,
    };

    const [customRangeOpen, setCustomRangeOpen] = useState(false);
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');

    // "Overall" = all-time. Anchored to a fixed date safely before any platform
    // data so every range-aware endpoint (incl. the days-based buyer-heat one)
    // effectively sees the full history.
    const OVERALL_START = '2020-01-01T00:00:00.000Z';

    const handleDateRangeChange = (range) => {
        if (range === 'custom') {
            const today = new Date();
            const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
            setCustomStart(monthAgo.toISOString().slice(0, 10));
            setCustomEnd(today.toISOString().slice(0, 10));
            setCustomRangeOpen(true);
            return;
        }

        setSelectedRange(range);
        const now = new Date();

        if (range === 'overall') {
            setDateRange({
                start: OVERALL_START,
                end: now.toISOString()
            });
            return;
        }

        const days = RANGE_DAYS[range] ?? 30;
        const start = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

        setDateRange({
            start: start.toISOString(),
            end: now.toISOString()
        });
    };

    const applyCustomRange = () => {
        if (!customStart || !customEnd) {
            toast.error('Please select both a start and end date');
            return;
        }
        const startDate = new Date(`${customStart}T00:00:00`);
        const endDate = new Date(`${customEnd}T23:59:59`);
        if (startDate > endDate) {
            toast.error('Start date must be before end date');
            return;
        }
        setSelectedRange('custom');
        setDateRange({ start: startDate.toISOString(), end: endDate.toISOString() });
        setCustomRangeOpen(false);
    };


    const handleBuyerClick = async (buyer) => {
        if (redirectingBuyer) return;
        setRedirectingBuyer(true);
        const toastId = toast.loading(`Opening ${buyer.buyer_name || buyer.buyer_email}'s profile...`);
        try {
            // CRM-contact rows (open-home walk-ins already in the CRM) carry a real
            // contact_id — open it directly. Portal buyers resolve via a virtual contact.
            let contactId = buyer.contact_id;
            if (!contactId) {
                const response = await syncVirtualContact(`buyer_${buyer.buyer_id}`);
                contactId = response?.data?.id;
            }
            if (!contactId) throw new Error('Could not resolve contact');
            toast.dismiss(toastId);
            navigate(`/contacts/${contactId}?tab=activity`);
        } catch (err) {
            toast.error('Could not open buyer profile. Please try again.', { id: toastId });
            console.error('handleBuyerClick error:', err);
        } finally {
            setRedirectingBuyer(false);
        }
    };

    const handleKPICardClick = (cardId, cardTitle) => {
        if (cardId === 'save_rate') {
            setSavesOpen(true);
            return;
        }
        if (ENGAGEMENT_VIEWS[cardId]) {
            setEngagementView(cardId);
            return;
        }
        switch (cardId) {
            case 'total_events':
                setEventModalConfig({
                    title: 'All Events',
                    eventName: null,
                    startDate: dateRange.start,
                    endDate: dateRange.end,
                });
                setEventModalOpen(true);
                break;
            case 'new_signups':
                setEventModalConfig({
                    title: 'New Buyer Signups (Latest First)',
                    eventName: 'signup_completed',
                    startDate: dateRange.start,
                    endDate: dateRange.end,
                });
                setEventModalOpen(true);
                break;
            case 'enquiries':
                setEnquiryModalOpen(true);
                break;
            case 'offers':
                setOfferModalOpen(true);
                break;
            case 'gallery_views':
                setEventModalConfig({
                    title: 'Gallery Views',
                    eventName: 'property_gallery_viewed',
                    startDate: dateRange.start,
                    endDate: dateRange.end,
                });
                setEventModalOpen(true);
                break;
            case 'intent_clicks':
                setEventModalConfig({
                    title: 'Intent Clicks (Phone/Email)',
                    eventName: 'call_clicked,email_clicked', // Comma-separated for multiple
                    startDate: dateRange.start,
                    endDate: dateRange.end,
                });
                setEventModalOpen(true);
                break;
            case 'shares':
                setEventModalConfig({
                    title: 'Property Shares — Who shared what',
                    eventName: 'share_clicked',
                    startDate: dateRange.start,
                    endDate: dateRange.end,
                });
                setEventModalOpen(true);
                break;
            case 'new_signups_breakdown':
                setSignupBreakdownOpen(true);
                break;
            case 'total_sessions':
            case 'avg_session_duration':
                setSessionModalOpen(true);
                break;
            default:
                console.log('Card clicked:', cardId);
        }
    };

    const handleFunnelStageClick = (eventName, stageName) => {
        if (eventName === 'enquiry_sent') {
            setEnquiryModalOpen(true);
            return;
        }
        setEventModalConfig({
            title: stageName,
            eventName: eventName,
            startDate: dateRange.start,
            endDate: dateRange.end,
        });
        setEventModalOpen(true);
    };

    return (
        <div className="min-h-screen bg-gray-50 w-full">
            {/* Header */}
            <div className="mb-6 p-3 sm:p-4 md:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="min-w-0">
                        <h2 className="text-base font-semibold text-gray-900">
                            {currentTab?.title || 'Analytics'}
                        </h2>
                        <p className="text-xs text-gray-500 mt-0.5">
                            {currentTab?.subtitle || ''}
                        </p>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                        {isAdmin && <LiveUsersBadge onClick={() => setLiveUsersModalOpen(true)} />}

                        <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 px-3 py-2 min-w-0">
                            <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <select
                                value={selectedRange}
                                onChange={(e) => handleDateRangeChange(e.target.value)}
                                className="border-none bg-transparent text-sm font-medium text-gray-700 focus:outline-none cursor-pointer"
                            >
                                <option value="overall">Overall (all time)</option>
                                <option value="1d">Last 1 day</option>
                                <option value="7d">Last 7 days</option>
                                <option value="30d">Last 30 days</option>
                                <option value="60d">Last 60 days</option>
                                <option value="90d">Last 90 days</option>
                                <option value="120d">Last 120 days</option>
                                <option value="180d">Last 180 days</option>
                                <option value="365d">Last 365 days</option>
                                <option value="custom">
                                    {selectedRange === 'custom' && customStart && customEnd
                                        ? `${customStart} → ${customEnd}`
                                        : 'Custom range…'}
                                </option>
                            </select>
                        </div>

                        <button
                            onClick={handleRefresh}
                            disabled={refreshing}
                            title="Refresh data"
                            className="flex items-center justify-center w-10 h-10 bg-white text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50 flex-shrink-0"
                        >
                            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                        </button>

                        {isAdmin && (
                            <button
                                onClick={() => setExportModalOpen(true)}
                                title="Export this analytics environment for a date range"
                                className="flex items-center gap-2 px-3 h-10 bg-white text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50 hover:text-teal-600 transition-colors flex-shrink-0"
                            >
                                <Download className="w-4 h-4" />
                                <span className="hidden sm:inline text-sm font-medium">Export</span>
                            </button>
                        )}

                        {isAdmin && (
                            <button
                                onClick={() => setExclusionsModalOpen(true)}
                                title="Analytics settings — exclude accounts"
                                className="flex items-center justify-center w-10 h-10 bg-white text-gray-700 rounded-lg border border-gray-200 hover:bg-gray-50 hover:text-blue-600 transition-colors flex-shrink-0"
                            >
                                <Settings className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>

                <div className="mt-4 flex bg-gray-100 p-1 rounded-xl w-full max-w-full overflow-x-auto sm:w-auto sm:inline-flex">
                    {visibleTabs.map((tab) => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveView(tab.id)}
                                className={`flex flex-shrink-0 sm:flex-none items-center justify-center gap-2 px-3 sm:px-4 py-1.5 rounded-lg text-xs sm:text-sm font-bold transition-all whitespace-nowrap ${
                                    activeView === tab.id
                                    ? 'bg-white text-blue-600 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <Icon className="w-4 h-4 flex-shrink-0" />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className={`p-3 sm:p-4 md:p-6 pt-0 transition-opacity ${refreshing ? 'opacity-60 pointer-events-none' : 'opacity-100'}`}>
                {refreshing && (
                    <div className="flex items-center justify-center gap-2 mb-3 text-xs font-medium text-blue-600">
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                        Updating data for selected range…
                    </div>
                )}
                {!isAgent && activeView === 'daily' ? (
                    <>
                        {/* Portal overview — THE headline KPIs for this page.
                            The Daily Report below used to repeat these cards
                            from nightly snapshots on its own window, so the two
                            grids printed different numbers for the same labels.
                            Everything now reads from one place, on the date
                            range selected above (Monday 13037765152). */}
                        <section className="mb-8">
                            <div className="mb-4">
                                <h2 className="text-lg font-bold text-gray-900">Overview</h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    Key metrics for the selected date range, each compared with the
                                    same length of time immediately before it.
                                </p>
                            </div>
                            <KPISummaryCards metrics={dashboardData} onCardClick={handleKPICardClick} isOverall={selectedRange === 'overall'} />

                            {/* Portal outcomes: DOMA-influenced commission attribution */}
                            <div className="mt-6">
                                <DomaInfluencedRevenue
                                    startDate={dateRange.start}
                                    endDate={dateRange.end}
                                />
                            </div>
                        </section>

                        {/* Long-window trends the live endpoints can't answer:
                            GeeVee, calculator usage and the nightly insights. */}
                        <section className="mt-8">
                            <div className="mb-4">
                                <h2 className="text-lg font-bold text-gray-900">Product usage &amp; insights</h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    GeeVee and calculator usage from the nightly snapshots, with what to
                                    improve on. Pick the comparison window below — it is independent of
                                    the date range above.
                                </p>
                            </div>
                            <DailyReportPanel />
                        </section>

                        {/* Commission summary (office-wide earnings & agent
                            leaderboard) now lives in the Agent Analytics tab's
                            overview, not on the Portal report (Monday 13058606178). */}

                        {/* Journey + all traffic sources */}
                        <section className="mt-8 mb-8">
                            <div className="mb-4">
                                <h2 className="text-lg font-bold text-gray-900">Buyer journey &amp; traffic</h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    Conversion funnel and signups by source. Click a Geo SEO or Traditional SEO
                                    row for the full channel breakdown (property views, revenue & per-page detail).
                                </p>
                            </div>
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <FunnelChart
                                    funnelData={dashboardData?.funnel}
                                    onStageClick={handleFunnelStageClick}
                                />
                                <SourceQualityChart
                                    sources={dashboardData?.top_sources}
                                    startDate={dateRange.start}
                                    endDate={dateRange.end}
                                />
                            </div>
                            {/* Open-home sign-ups by agent now live inside the
                                "Real Estate Agents" Traffic Sources drill-down
                                (click that row in SourceQualityChart). */}
                        </section>
                    </>
                ) : activeView === 'property' ? (
                    <>
                        {/* Property engagement — the only thing on the Property tab now */}
                        <section className="mb-6">
                            <div className="mb-4">
                                <h2 className="text-lg font-bold text-gray-900">Top performing properties</h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    Listings with the most buyer interactions in this period.
                                </p>
                            </div>
                            <PropertyInteractionStats
                                properties={propertyInteractions}
                                loading={propertyLoading && !propertyInteractions.length}
                                refreshing={propertyLoading && propertyInteractions.length > 0}
                                sortBy={propertySortBy}
                                onSortChange={setPropertySortBy}
                                statusFilter={propertyStatus}
                                onStatusChange={setPropertyStatus}
                                searchQuery={propertySearch}
                                onSearchChange={setPropertySearch}
                            />
                        </section>
                    </>
                ) : activeView === 'buyer' ? (
                    <>
                        {/* Find Buyers for a Property — sits at the top of the
                            buyer analytics page: match a listing (and surrounding
                            suburbs) against saved buyer preferences */}
                        <div className="mb-8">
                            <FindBuyersForProperty />
                        </div>

                        {/* Top Engaged Buyers (Buyer Intent Score) */}
                        <div className="mb-8">
                            <TopEngagedBuyers
                                key={`teb-${buyerRefreshKey}`}
                                days={rangeDaysFromSelection()}
                            />
                        </div>

                        {/* Targeting brief — sourcing checklist */}
                        <div className="mb-8">
                            <TargetingBriefPanel
                                startDate={dateRange.start}
                                endDate={dateRange.end}
                            />
                        </div>

                        {/* Investor Demand Index — what investors are looking for */}
                        <div className="mb-8">
                            <InvestorDemandIndexPanel
                                startDate={dateRange.start}
                                endDate={dateRange.end}
                            />
                        </div>

                        {/* Off-Market Demand Index — most wanted off-market areas */}
                        <div className="mb-8">
                            <OffMarketDemandPanel />
                        </div>

                        <BuyerAnalytics
                            onBuyerClick={handleBuyerClick}
                            startDate={dateRange.start}
                            endDate={dateRange.end}
                        />

                    </>
                ) : activeView === 'renter' ? (
                    <>
                        {/* Find Renters for a Property — the lease-listing sibling
                            of Find Buyers above (Monday 12908991007) */}
                        <div className="mb-8">
                            <FindRentersForProperty />
                        </div>

                        {/* Renter preference charts. The Suburb Preferences card
                            (ranked by demand) stands in for a separate Targeting
                            Brief-style "priority suburbs" panel — Renter
                            Analytics is scoped to what RentingPreference
                            actually has, not a line-for-line port of every
                            Buyer Analytics sub-feature (no renter equivalent of
                            Top Engaged Buyers/intent score, Investor Demand
                            Index, or Off-Market Demand Index). */}
                        <RenterAnalytics
                            onRenterClick={handleBuyerClick}
                            startDate={dateRange.start}
                            endDate={dateRange.end}
                        />
                    </>
                ) : !isAgent && activeView === 'calculators' ? (
                    <div className="space-y-10">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-1">Investor Tools</h2>
                            <p className="text-sm text-gray-500 mb-6">
                                All 14 investment calculators on the buyer portal — usage by calculator, results and leads
                            </p>
                            <InvestmentCalculatorAnalytics
                                startDate={dateRange.start}
                                endDate={dateRange.end}
                            />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-1">Mortgage Calculator</h2>
                            <p className="text-sm text-gray-500 mb-6">
                                Repayments, borrowing capacity and broker enquiries
                            </p>
                            <MortgageCalculatorAnalytics
                                startDate={dateRange.start}
                                endDate={dateRange.end}
                            />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 mb-1">SMSF Calculator</h2>
                            <p className="text-sm text-gray-500 mb-6">
                                Buying property through a self-managed super fund
                            </p>
                            <SmsfAnalytics
                                startDate={dateRange.start}
                                endDate={dateRange.end}
                            />
                        </div>
                    </div>
                ) : canSeeTab('agents') && activeView === 'agents' ? (
                    /* Self-fetching: owns its own FY/period selector, so it does
                       not take the page-wide date range. */
                    <AgentAnalyticsPanel reloadToken={exclusionsVersion} />
                ) : canSeeTab('crm_overview') && activeView === 'crm_overview' ? (
                    <CrmOverviewPanel
                        onOpenAgentUsage={() => setActiveView('agent_usage')}
                        reloadToken={exclusionsVersion}
                    />
                ) : canSeeTab('agent_usage') && activeView === 'agent_usage' ? (
                    <AgentUsagePanel reloadToken={exclusionsVersion} />
                ) : !isAgent && activeView === 'engagement' ? (
                    <EngagementPanel
                        startDate={dateRange.start}
                        endDate={dateRange.end}
                    />
                ) : !isAgent && activeView === 'profiles' ? (
                    <div className="space-y-6">
                        {/* "Are you still actually looking to buy?" — live
                            totals, so it sits above the date-ranged panel
                            rather than inside it (Monday 13068998532). */}
                        <BuyerSearchStatusPanel />
                        <UserProfilesPanel
                            startDate={dateRange.start}
                            endDate={dateRange.end}
                        />
                    </div>
                ) : !isAgent && activeView === 'portfolio' ? (
                    <InvestorAnalyticsPanel
                        startDate={dateRange.start}
                        endDate={dateRange.end}
                    />
                ) : !isAgent && activeView === 'location' ? (
                    <LocationAnalyticsPanel
                        startDate={dateRange.start}
                        endDate={dateRange.end}
                    />
                ) : !isAgent && activeView === 'heatmap' ? (
                    <HeatmapPanel
                        startDate={dateRange.start}
                        endDate={dateRange.end}
                    />
                ) : !isAgent && activeView === 'cost_per_lead' ? (
                    <CostPerLeadPanel
                        startDate={dateRange.start}
                        endDate={dateRange.end}
                    />
                ) : !isAgent && activeView === 'suburb_research' ? (
                    <SuburbResearchPanel
                        startDate={dateRange.start}
                        endDate={dateRange.end}
                    />
                ) : canSeeTab('communications') && activeView === 'communications' ? (
                    <CommunicationsAnalyticsPanel />
                ) : canSeeTab('campaign_leads') && activeView === 'campaign_leads' ? (
                    <CampaignLeadsPanel
                        startDate={dateRange.start}
                        endDate={dateRange.end}
                    />
                ) : canSeeTab('facebook_leads') && activeView === 'facebook_leads' ? (
                    <FacebookLeadsPanel
                        startDate={dateRange.start}
                        endDate={dateRange.end}
                    />
                ) : canSeeTab(activeView) && sectionForTab(activeView) ? (
                    /* Every per-domain CRM tab and the Executive views render
                       through one panel — the API returns the same envelope for
                       all of them, so the tab config is the only thing that
                       differs. Self-fetching: it owns its own period selector.

                       There are deliberately no exec_agents / exec_marketing
                       branches any more: those tabs rendered this page's Agents
                       and Cost Per Lead panels a second time, which is what made
                       Executive read as a copy of CRM Analytics. */
                    <CrmSectionPanel
                        section={sectionForTab(activeView)}
                        reloadToken={exclusionsVersion}
                    />
                ) : !isAgent && activeView === 'chatbot' ? (
                    <ChatbotAnalyticsPanel
                        days={Math.max(1, Math.ceil((new Date(dateRange.end) - new Date(dateRange.start)) / (24 * 60 * 60 * 1000)))}
                        beforeRecent={
                            isAdmin ? (
                                <GeeVeeCategoriesPanel
                                    startDate={dateRange.start}
                                    endDate={dateRange.end}
                                />
                            ) : null
                        }
                    />
                ) : null}
            </div>

            {/* Modals */}
            <EventDetailModal
                isOpen={eventModalOpen}
                onClose={() => setEventModalOpen(false)}
                eventName={eventModalConfig.eventName}
                startDate={eventModalConfig.startDate}
                endDate={eventModalConfig.endDate}
                title={eventModalConfig.title}
            />
            <BuyerActivityModal
                isOpen={buyerModalOpen}
                onClose={() => setBuyerModalOpen(false)}
                buyerId={buyerModalConfig.buyerId}
                buyerEmail={buyerModalConfig.buyerEmail}
            />
            <EnquiryDetailModal
                isOpen={enquiryModalOpen}
                onClose={() => setEnquiryModalOpen(false)}
                startDate={dateRange.start}
                endDate={dateRange.end}
            />
            <OfferDetailModal
                isOpen={offerModalOpen}
                onClose={() => setOfferModalOpen(false)}
                startDate={dateRange.start}
                endDate={dateRange.end}
            />
            <BuyerEngagementModal
                isOpen={!!engagementView}
                onClose={() => setEngagementView(null)}
                viewId={engagementView}
                startDate={dateRange.start}
                endDate={dateRange.end}
                onBuyerClick={(b) => handleBuyerClick({ buyer_id: b.id, buyer_name: b.name, buyer_email: b.email })}
            />
            <PropertySavesModal
                isOpen={savesOpen}
                onClose={() => setSavesOpen(false)}
                startDate={dateRange.start}
                endDate={dateRange.end}
                onBuyerClick={(s) => handleBuyerClick({ buyer_id: s.buyer_id, buyer_name: s.buyer_name, buyer_email: s.buyer_email })}
            />
            <SignupBreakdownModal
                isOpen={signupBreakdownOpen}
                onClose={() => setSignupBreakdownOpen(false)}
                stats={dashboardData?.signup_stats}
            />
            <LiveUsersModal
                isOpen={liveUsersModalOpen}
                onClose={() => setLiveUsersModalOpen(false)}
            />
            <SessionDetailModal
                isOpen={sessionModalOpen}
                onClose={() => setSessionModalOpen(false)}
                startDate={dateRange.start}
                endDate={dateRange.end}
            />
            <AnalyticsExclusionsModal
                isOpen={exclusionsModalOpen}
                onClose={() => setExclusionsModalOpen(false)}
                onChanged={() => {
                    if (!isAgent && activeView === 'daily') loadDashboardMetrics();
                    setExclusionsVersion((v) => v + 1);
                }}
            />

            {/* Export. Opens on the environment and range already on screen,
                because that is nearly always the export the user wants. An
                "Overall" range is left out: the modal defaults to the last 30
                days rather than six years of every tab. */}
            <AnalyticsExportModal
                isOpen={exportModalOpen}
                onClose={() => setExportModalOpen(false)}
                environment={environment}
                dateRange={dateRange}
            />

            {customRangeOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                        <div className="flex items-center justify-between p-5 border-b border-gray-200">
                            <h3 className="text-lg font-bold text-gray-900">Custom date range</h3>
                            <button
                                onClick={() => setCustomRangeOpen(false)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                aria-label="Close"
                            >
                                <span className="text-gray-500 text-xl leading-none">×</span>
                            </button>
                        </div>
                        <div className="p-5 space-y-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">Start date</label>
                                <input
                                    type="date"
                                    value={customStart}
                                    max={customEnd || undefined}
                                    onChange={(e) => setCustomStart(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1">End date</label>
                                <input
                                    type="date"
                                    value={customEnd}
                                    min={customStart || undefined}
                                    max={new Date().toISOString().slice(0, 10)}
                                    onChange={(e) => setCustomEnd(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                />
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
                            <button
                                onClick={() => setCustomRangeOpen(false)}
                                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={applyCustomRange}
                                className="px-5 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AnalyticsPage;
